import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  LinearProgress,
  Tooltip,
  Stack,
  Divider,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeleteIcon from '@mui/icons-material/Delete';
import { ACCOUNTING_API } from '../../../../config/api';
import { accountingApi } from '../../../../config/apiClient';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LoanInstallmentPayModal, { InstallmentForPay, LoanForPay } from './LoanInstallmentPayModal';

interface LoanDetailModalProps {
  open: boolean;
  onClose: () => void;
  loanId: number | null;
  onChanged?: () => void;
}

const LoanDetailModal: React.FC<LoanDetailModalProps> = ({ open, onClose, loanId, onChanged }) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [loan, setLoan] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Taksit ödeme modalı
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payInstallment, setPayInstallment] = useState<InstallmentForPay | null>(null);

  // Ödeme silme onayı
  const [deletePaymentTarget, setDeletePaymentTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const loadLoan = useCallback(async () => {
    if (!loanId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await accountingApi.get(ACCOUNTING_API.BANK_LOAN_BY_ID(loanId));
      setLoan(res.data?.data || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    if (open && loanId) {
      setLoan(null);
      loadLoan();
    }
  }, [open, loanId, loadLoan]);

  const fmt = (n: number) => Number(n || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(localeCode) : '-');

  const handleChanged = () => {
    loadLoan();
    onChanged?.();
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentTarget?.paymentId) return;
    try {
      setDeleting(true);
      await accountingApi.post(ACCOUNTING_API.BANK_LOAN_PAYMENT_DELETE, {
        PaymentId: deletePaymentTarget.paymentId,
        DeletedBy: userDetails?.email || 'Sistem',
      });
      setDeletePaymentTarget(null);
      handleChanged();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
      setDeletePaymentTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOverdueInst = (inst: any) => inst.status === 'PENDING' && new Date(inst.dueDate) < today;

  const currency = loan?.currency || 'TRY';
  const paidTotal = Number(loan?.paidTotal || 0);
  const totalPayback = Number(loan?.totalPayback || 0);
  const progress = totalPayback > 0 ? Math.min(100, (paidTotal / totalPayback) * 100) : 0;

  const disbursementLabel = () => {
    if (!loan) return '-';
    if (loan.disbursementTarget === 'BANK') return `${tt('Bankaya aktarıldı', 'Transferred to bank')}: ${loan.disbursementBankAccountName || '-'}`;
    if (loan.disbursementTarget === 'CASHBOX') return `${tt('Kasaya aktarıldı', 'Transferred to cashbox')}: ${loan.disbursementCashboxName || '-'}`;
    return tt('Aktarılmadı', 'Not transferred');
  };

  const sourceLabel = (src?: string | null) => {
    if (src === 'cashbox') return tt('Kasa', 'Cashbox');
    if (src === 'bank') return tt('Banka', 'Bank');
    if (src === 'accrual') return tt('Tahakkuk', 'Accrual');
    return src || '-';
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {tt('Kredi Detayı', 'Loan Detail')}{loan ? ` — ${loan.name}` : ''}
            </Typography>
            <IconButton edge="end" onClick={onClose}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && loan && (
            <>
              {/* Üst özet bloğu */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">{tt('Anapara', 'Principal')}</Typography>
                    <Typography variant="body1" fontWeight={600}>{fmt(loan.principalAmount)} {currency}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">{tt('Toplam Geri Ödeme', 'Total Payback')}</Typography>
                    <Typography variant="body1" fontWeight={600}>{fmt(loan.totalPayback)} {currency}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">{tt('Toplam Faiz', 'Total Interest')}</Typography>
                    <Typography variant="body1" fontWeight={600} color="warning.main">{fmt(loan.totalInterest)} {currency}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">{tt('Net Kullandırım', 'Net Disbursement')}</Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">{fmt(loan.netDisbursedAmount)} {currency}</Typography>
                  </Box>
                </Box>

                {(loan.deductions || []).length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="textSecondary">{tt('Kesintiler', 'Deductions')}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                      {(loan.deductions || []).map((d: any) => (
                        <Chip key={d.id} size="small" label={`${d.name}: ${fmt(d.amount)} ${currency}`} />
                      ))}
                    </Stack>
                  </>
                )}

                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    {tt('Banka', 'Bank')}: <b>{loan.bankName || loan.bankAccountName || '-'}</b>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {tt('Kullandırım', 'Disbursement')}: <b>{disbursementLabel()}</b>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {tt('Kredi No', 'Loan No')}: <b>{loan.loanNumber || '-'}</b> — {tt('Tarih', 'Date')}: <b>{fmtDate(loan.loanDate)}</b>
                  </Typography>
                  {loan.notes && (
                    <Typography variant="body2" color="textSecondary">
                      {tt('Notlar', 'Notes')}: {loan.notes}
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* İlerleme çubuğu */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="textSecondary">
                    {tt('Ödenen', 'Paid')}: {fmt(paidTotal)} / {fmt(totalPayback)} {currency} ({progress.toFixed(0)}%)
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {loan.paidInstallmentCount}/{loan.totalInstallmentCount} {tt('taksit', 'installments')}
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
              </Box>

              {/* Taksit takvimi */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>No</TableCell>
                      <TableCell>{tt('Vade', 'Due Date')}</TableCell>
                      <TableCell align="right">{tt('Tutar', 'Amount')}</TableCell>
                      <TableCell>{tt('Durum', 'Status')}</TableCell>
                      <TableCell align="right">{tt('İşlem', 'Action')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(loan.installments || []).map((inst: any) => {
                      const overdue = isOverdueInst(inst);
                      return (
                        <TableRow key={inst.id} sx={overdue ? { bgcolor: 'error.50' } : undefined}>
                          <TableCell>{inst.installmentNo}</TableCell>
                          <TableCell>{fmtDate(inst.dueDate)}</TableCell>
                          <TableCell align="right">
                            {fmt(inst.amount)} {currency}
                            {(inst.principalPart || inst.interestPart) && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {tt('Anapara', 'Principal')}: {fmt(inst.principalPart || 0)} / {tt('Faiz', 'Interest')}: {fmt(inst.interestPart || 0)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {inst.status === 'PAID' ? (
                              <Tooltip
                                title={
                                  `${tt('Ödeme Tarihi', 'Paid At')}: ${fmtDate(inst.paidAt)} — ${tt('Kaynak', 'Source')}: ${sourceLabel(inst.paymentSource)}` +
                                  (Number(inst.lateInterestAmount || 0) > 0 ? ` — ${tt('Gecikme Faizi', 'Late Interest')}: ${fmt(inst.lateInterestAmount)} ${currency}` : '') +
                                  (inst.paidBy ? ` — ${inst.paidBy}` : '')
                                }
                              >
                                <Chip size="small" color="success" label={tt('Ödendi', 'Paid')} />
                              </Tooltip>
                            ) : overdue ? (
                              <Chip size="small" color="error" label={tt('Gecikmiş', 'Overdue')} />
                            ) : (
                              <Chip size="small" variant="outlined" label={tt('Bekliyor', 'Pending')} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {inst.status === 'PENDING' && loan.status === 'ACTIVE' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<PaymentsIcon fontSize="small" />}
                                onClick={() => {
                                  setPayInstallment({
                                    id: inst.id,
                                    installmentNo: inst.installmentNo,
                                    dueDate: inst.dueDate,
                                    amount: Number(inst.amount),
                                  });
                                  setPayModalOpen(true);
                                }}
                              >
                                {tt('Öde', 'Pay')}
                              </Button>
                            )}
                            {inst.status === 'PAID' && inst.paymentId && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon fontSize="small" />}
                                onClick={() => setDeletePaymentTarget(inst)}
                              >
                                {tt('Ödemeyi Sil', 'Delete Payment')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{tt('Kapat', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Taksit ödeme modalı */}
      <LoanInstallmentPayModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        onSuccess={handleChanged}
        loan={loan ? ({ id: loan.id, name: loan.name, currency } as LoanForPay) : null}
        installment={payInstallment}
      />

      {/* Ödeme silme (HARD DELETE) onayı */}
      <Dialog open={Boolean(deletePaymentTarget)} onClose={deleting ? undefined : () => setDeletePaymentTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Ödemeyi Sil', 'Delete Payment')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tt(
              `Taksit ${deletePaymentTarget?.installmentNo} ödemesi kalıcı olarak silinecek (geri alınamaz). Kasadan/bankadan yapıldıysa ilgili kasa/banka hareketi de silinecek ve bakiye geri düzeltilecek. Taksit tekrar "Bekliyor" durumuna dönecek.`,
              `Payment of installment ${deletePaymentTarget?.installmentNo} will be permanently deleted (cannot be undone). If it was paid from a cashbox/bank, the related transaction will also be deleted and the balance restored. The installment will return to "Pending".`
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePaymentTarget(null)} disabled={deleting}>{tt('İptal', 'Cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeletePayment}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {tt('Kalıcı Olarak Sil', 'Delete Permanently')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LoanDetailModal;
